import { useCallback, useEffect, useRef, useState } from 'react'
import { apiFetch } from '@/utils/apiFetch'
import { apiUrl } from '@/utils/apiUrl'

const SOURCES = {
  pnl: '/api/daily-pnl',
  risk: '/api/circuit-breakers',
  heartbeat: '/api/heartbeat/status',
}
const initialResources = Object.fromEntries(
  Object.keys(SOURCES).map((key) => [key, { data: null, error: null, receivedAt: null }]),
)

export function useTradingOverview(intervalSeconds) {
  const [resources, setResources] = useState(initialResources)
  const [refreshing, setRefreshing] = useState(true)
  const active = useRef(null)

  const refresh = useCallback(async () => {
    active.current?.abort()
    const controller = new AbortController()
    active.current = controller
    const timeout = setTimeout(() => controller.abort('timeout'), 15000)
    setRefreshing(true)
    const entries = Object.entries(SOURCES)
    const results = await Promise.allSettled(
      entries.map(async ([key, path]) => {
        const response = await apiFetch(apiUrl(path), {
          signal: controller.signal,
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const data = await response.json()
        if (
          (key === 'pnl' && !Array.isArray(data)) ||
          (key === 'risk' && !Array.isArray(data?.accounts)) ||
          (key === 'heartbeat' && (!data?.statuses || typeof data.statuses !== 'object'))
        ) {
          throw new Error('Unexpected response format')
        }
        return { data, error: null, receivedAt: Date.now() }
      }),
    )
    clearTimeout(timeout)
    if (active.current !== controller) return
    setResources((previous) =>
      Object.fromEntries(
        entries.map(([key], index) => {
          const result = results[index]
          return [
            key,
            result.status === 'fulfilled'
              ? result.value
              : {
                  ...previous[key],
                  error: controller.signal.aborted
                    ? 'Request timed out'
                    : result.reason?.message || 'Connection unavailable',
                },
          ]
        }),
      ),
    )
    setRefreshing(false)
  }, [])

  useEffect(() => {
    refresh()
    const timer =
      intervalSeconds > 0
        ? setInterval(() => {
            if (!document.hidden && navigator.onLine) refresh()
          }, intervalSeconds * 1000)
        : null
    const resume = () => {
      if (intervalSeconds > 0 && !document.hidden && navigator.onLine) refresh()
    }
    window.addEventListener('online', resume)
    document.addEventListener('visibilitychange', resume)
    return () => {
      if (timer) clearInterval(timer)
      window.removeEventListener('online', resume)
      document.removeEventListener('visibilitychange', resume)
      const controller = active.current
      active.current = null
      controller?.abort()
    }
  }, [intervalSeconds, refresh])

  return { resources, refreshing, refresh }
}
